import { Cache } from "memory-cache";
import { load } from "js-yaml";
import { Mutex } from "async-mutex";
import * as Papa from "papaparse";
import { Client } from "./esiclient";
// Cache
const cache = new Cache();
const esiClient = new Client();

// Mutexes
var typesMutex = new Mutex();
var stationsMutex = new Mutex();
var priceMutex = new Mutex();

interface nameInterface {
  entityName: string;
  id: number;
}

export class StaticStorageClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    const assetURL =
      window.location.host.startsWith("localhost") || window.location.host.startsWith("exceldevstc")
        ? window.location.host.startsWith("localhost")
          ? "/assets" /* Dev */
          : "/$web/app/assets" /* Test */
        : "/$web/app/assets"; /* Prod */
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : assetURL; //staticStorageURL;
  }

  // Prices
  async get_prices() {
    return await priceMutex.runExclusive(async () => {
      let prices = await cache.get("prices");
      if (prices) {
        return prices;
      }
      try {
        const esiPrices = await esiClient.get_markets_prices(null, null);
        if (esiPrices !== null) cache.put("prices", esiPrices, 60 * 60 * 1000 /* 1 hour */);
        return esiPrices;
      } catch (e) {
        return null;
      }
    });
  }

  async get_names(array: number[]) {
    return await priceMutex.runExclusive(async () => {
      let names: nameInterface[] = [];
      let missingNames: number[] = [];
      for (const y in array) {
        let name = await cache.get("name-" + array[y]);
        if (name && typeof name == "string") {
          const entry: nameInterface = { entityName: name, id: array[y] };
          names.push(entry);
        } else missingNames.push(array[y]);
      }
      if (!missingNames.length) return names;
      try {
        const esiNames = await esiClient.post_universe_names(null, missingNames);
        if (esiNames !== null) {
          esiNames.forEach((res: any) => {
            const esiEntry: nameInterface = { entityName: res.name, id: res.id };
            names.push(esiEntry);
            cache.put("name-" + res.id, res.name, 12 * 60 * 60 * 1000);
          });
        }
        return names;
      } catch (e) {
        return null;
      }
    });
  }

  // Blueprints
  async get_blueprints() {
    return await typesMutex.runExclusive(async () => {
      let blueprints = await cache.get("blueprints");
      if (blueprints) {
        return blueprints;
      }
      // Get blueprints YAML file from Azure storage
      //let url = `${this.baseUrl}/sde/fsd/blueprints.yaml`;
      let url = `${this.baseUrl}/blueprints.yaml`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert YAML to JSON
      blueprints = await load(text);
      // Save to cache
      cache.put("blueprints", blueprints, 60 * 60 * 1000 /* 1 hour */);
      return blueprints;
    });
  }

  async get_blueprints_blueprint_id(blueprint_id: number) {
    const blueprints: any = await this.get_blueprints();
    if (Object.prototype.hasOwnProperty.call(blueprints, blueprint_id.toString())) {
      return blueprints[blueprint_id.toString()];
    } else {
      return null;
    }
  }

  async get_blueprints_type_id(type_id: number): Promise<any | null> {
    const findBlueprint = (blueprints: any, predicate: (arg0: number) => any | null) =>
      Object.keys(blueprints).find((k) => predicate(blueprints[k]));

    const isBlueprintForType = (blueprint: any, type_id: number): boolean => {
      for (const activity of ["manufacturing", "reaction"]) {
        if (blueprint.activities && blueprint.activities[activity] && blueprint.activities[activity].products) {
          const type = blueprint.activities[activity].products.find((p: any) => p.typeID === type_id);
          if (type) {
            return true;
          }
        }
      }
      return false;
    };
    const blueprints: any = await this.get_blueprints();
    let blueprint_id = findBlueprint(blueprints, (b: any) => isBlueprintForType(b, type_id));
    if (type_id == 16672) blueprint_id = (46207).toString();
    return blueprint_id ? blueprints[blueprint_id.toString()] : null;
  }

  // Stations
  async get_stations() {
    return await stationsMutex.runExclusive(async () => {
      let stations = await cache.get("stations");
      if (stations) {
        return stations;
      }
      // Get stations YAML file from Azure storage
      let url = `${this.baseUrl}/npcStations_named.yaml`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert YAML to JSON
      stations = await load(text);
      // Save to cache
      cache.put("stations", stations, 60 * 60 * 1000 /* 1 hour */);
      return stations;
    });
  }

  async get_stations_station_id(station_id: number) {
    const stations: any = await this.get_stations();
    if (stations !== null) {
      if (Object.prototype.hasOwnProperty.call(stations, station_id)) {
        return stations[station_id];
      } else {
        return null;
      }
    }
  }
  async get_stations_in_system(system_id: number) {
    const stations: any = await this.get_stations();
    if (stations !== null) {
      return (
        Object.entries(stations)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([_, obj]: any) => obj.solarSystemID === system_id)
          .map(([id, obj]: any) => ({ ...obj, stationID: Number(id) }))
      );
    } else {
      return null;
    }
  }
  async get_stations_by_string(search: string, strict: boolean) {
    const data: any = await this.get_stations();
    const list = Object.entries(data)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, obj]: any) => obj.name !== undefined)
      .map(([id, obj]: any) => ({ ...obj, stationID: Number(id) }));
    const filteredDataSoICanSearch = list.filter((x: any) => typeof x.name == "string" && x.name !== "No Station Name");
    if (search == null) {
      return filteredDataSoICanSearch;
    }
    let stations = undefined;
    if (strict) {
      stations = filteredDataSoICanSearch.filter((x: any) => x.name.toString().toLowerCase() == search.toLowerCase());
    } else {
      stations = filteredDataSoICanSearch.filter(
        (x: any) => x.name.toString().toLowerCase().indexOf(search.toLowerCase()) > -1
      );
    }
    if (stations !== undefined) {
      return stations;
    } else {
      return null;
    }
  }
  // type material
  async get_type_material() {
    return await stationsMutex.runExclusive(async () => {
      let type_material = await cache.get("type_material");
      if (type_material) {
        return type_material;
      }
      let url = `${this.baseUrl}/typeMaterials.yaml`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert YAML to JSON
      type_material = await load(text);
      // Save to cache
      cache.put("type_material", type_material, 60 * 60 * 1000 /* 1 hour */);
      return type_material;
    });
  }

  async get_type_material_by_id(type_id: number) {
    const materials: any = await this.get_type_material();
    if (Object.prototype.hasOwnProperty.call(materials, type_id.toString())) {
      return materials[type_id.toString()];
    } else {
      return null;
    }
  }
  //hoboleaks repackaged volume
  //https://sde.hoboleaks.space/tq/repackagedvolumes.json
  async get_repackaged_volume() {
    return await stationsMutex.runExclusive(async () => {
      let repackaged_volume = await cache.get("repackaged_volume");
      if (repackaged_volume) {
        return repackaged_volume;
      }
      const url = `${this.baseUrl}/repackagedvolumes.json`; //"https://sde.hoboleaks.space/tq/repackagedvolumes.json";
      try {
        const response = checkStatus(await fetch(url));
        repackaged_volume = JSON.parse(await response.text());
        // Save to cache
        cache.put("repackaged_volume", repackaged_volume, 60 * 60 * 1000 /* 1 hour */);
        return repackaged_volume;
      } catch (e) {
        cache.put("repackaged_volume", [], 60 * 60 * 1000 /* 1 hour */);
        return null;
      }
    });
  }
  async get_skills_dogma() {
    return await stationsMutex.runExclusive(async () => {
      let data = await cache.get("skills_dogma");
      if (data) {
        return data;
      }
      const url = `${this.baseUrl}/dogma_category_16.json`; //"https://sde.hoboleaks.space/tq/repackagedvolumes.json";
      try {
        const response = checkStatus(await fetch(url));
        data = JSON.parse(await response.text());
        // Save to cache
        cache.put("skills_dogma", data, 60 * 60 * 1000 /* 1 hour */);
        return data;
      } catch (e) {
        cache.put("skills_dogma", [], 60 * 60 * 1000 /* 1 hour */);
        return null;
      }
    });
  }
  async get_dogma_6() {
    return await stationsMutex.runExclusive(async () => {
      let data = await cache.get("dogma_6");
      if (data) {
        return data;
      }
      const url = `${this.baseUrl}/dogma_category_6.json`; //"https://sde.hoboleaks.space/tq/repackagedvolumes.json";
      try {
        const response = checkStatus(await fetch(url));
        data = JSON.parse(await response.text());
        // Save to cache
        cache.put("dogma_6", data, 60 * 60 * 1000 /* 1 hour */);
        return data;
      } catch (e) {
        cache.put("dogma_6", [], 60 * 60 * 1000 /* 1 hour */);
        return null;
      }
    });
  }
  async get_dogma_7() {
    return await stationsMutex.runExclusive(async () => {
      let data = await cache.get("dogma_7");
      if (data) {
        return data;
      }
      const url = `${this.baseUrl}/dogma_category_7.json`; //"https://sde.hoboleaks.space/tq/repackagedvolumes.json";
      try {
        const response = checkStatus(await fetch(url));
        data = JSON.parse(await response.text());
        // Save to cache
        cache.put("dogma_7", data, 60 * 60 * 1000 /* 1 hour */);
        return data;
      } catch (e) {
        cache.put("dogma_7", [], 60 * 60 * 1000 /* 1 hour */);
        return null;
      }
    });
  }

  async get_repackaged_volume_by_id(type_id: number) {
    const repackaged_volume: any = await this.get_repackaged_volume();
    if (Object.prototype.hasOwnProperty.call(repackaged_volume, type_id.toString())) {
      return repackaged_volume[type_id.toString()];
    } else {
      return null;
    }
  }

  // Factions
  async get_factions() {
    return await typesMutex.runExclusive(async () => {
      let data = await cache.get("factions");
      if (data) {
        return data;
      }
      // Get types CSV file from service
      let url = `${this.baseUrl}/chrFactions.csv`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert CSV to JSON array
      data = getJsonObjects(
        Papa.parse(text, {
          dynamicTyping: true,
        })
      );
      // Save to cache
      cache.put("factions", data, 60 * 60 * 1000 /* 1 hour */);
      return data;
    });
  }

  async get_faction_by_id(faction_id: number) {
    const data: any = await this.get_factions();
    if (Object.prototype.hasOwnProperty.call(data, faction_id)) {
      return data[faction_id];
    } else {
      return null;
    }
  }
  // Categories
  async get_categories() {
    return await typesMutex.runExclusive(async () => {
      let data = await cache.get("categories");
      if (data) {
        return data;
      }
      // Get types CSV file from service
      let url = `${this.baseUrl}/categories.yaml`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert CSV to JSON array
      const categories = await load(text);
      // Save to cache
      cache.put("categories", categories, 60 * 60 * 1000 /* 1 hour */);
      return categories;
    });
  }

  async get_category_by_id(category_id: number) {
    const data: any = await this.get_categories();
    if (Object.prototype.hasOwnProperty.call(data, category_id)) {
      return data[category_id];
    } else {
      return null;
    }
  }

  async get_groups_in_category(category_id: number) {
    const groups: any = await this.get_groups();
    if (groups !== null) {
      return (
        Object.entries(groups)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([_, obj]: any) => obj.categoryID === category_id)
          .map(([id, obj]: any) => ({ ...obj, groupID: Number(id) }))
      );
    } else {
      return null;
    }
  }

  // Groups
  async get_groups() {
    return await typesMutex.runExclusive(async () => {
      let data = await cache.get("groups");
      if (data) {
        return data;
      }
      // Get types YAML file from service
      let url = `${this.baseUrl}/groups.yaml`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert YAML to JSON array
      const groups = await load(text);
      // Save to cache
      cache.put("groups", groups, 60 * 60 * 1000 /* 1 hour */);
      return groups;
    });
  }

  async get_groups_by_id(group_id: number) {
    const groups: any = await this.get_groups();
    if (Object.prototype.hasOwnProperty.call(groups, group_id)) {
      return groups[group_id];
    } else {
      return null;
    }
  }
  // Types
  async get_types() {
    return await typesMutex.runExclusive(async () => {
      let types: any = await cache.get("types");
      if (types) {
        return types;
      }
      // Get types json file from service
      let url = `${this.baseUrl}/types.json`;
      const response = checkStatus(await fetch(url));
      const json = JSON.parse(await response.text());
      // Save to cache
      cache.put("types", json, 60 * 60 * 1000 /* 1 hour */);
      return json;
    });
  }

  async get_types_type_id(type_id: number) {
    const types: any = await this.get_types();
    if (types) {
      return types.find((x: any) => x.typeID === type_id);
    } else {
      return null;
    }
  }
  async get_types_by_string(type_name: string, strict: boolean, published_only?: boolean) {
    const types: Object[] | null = await this.get_types();
    if (!types) return null;
    let publishedStrict = true;
    if (published_only === true) publishedStrict = false;
    if (strict) {
      let filteredExact = null;
      if (publishedStrict) {
        filteredExact = Object.values(types).filter(
          (obj: any) => obj.name?.toString().toLowerCase() === type_name.toLowerCase() && obj.published
        );
      } else {
        filteredExact = Object.values(types).filter(
          (obj: any) => obj.name?.toString().toLowerCase() === type_name.toLowerCase()
        );
      }
      if (filteredExact.length) return filteredExact;
      else return null;
    } else {
      let filtered = null;
      if (publishedStrict) {
        filtered = types.filter(
          (obj: any) => obj.name?.toString().toLowerCase().includes(type_name.toLowerCase()) && obj.published
        );
      } else {
        filtered = types.filter((obj: any) => obj.name?.toString().toLowerCase().includes(type_name.toLowerCase()));
      }
      if (filtered.length) return filtered;
      return null;
    }
  }
  async get_types_in_groups(groupID: number) {
    const types: any = await this.get_types();
    if (types !== null) {
      return Object.values(types).filter((obj: any) => obj.groupID === groupID);
    } else {
      return null;
    }
  }

  async get_schematics() {
    return await typesMutex.runExclusive(async () => {
      let data = await cache.get("schematics");
      if (data) {
        return data;
      }
      let url = `${this.baseUrl}/planetSchematicsTypeMap.csv`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert CSV to JSON array
      data = Papa.parse(text, {
        dynamicTyping: true,
        header: true,
      });
      // Save to cache
      cache.put("schematics", data, 60 * 60 * 1000 /* 1 hour */);
      return data;
    });
  }
  async get_schematics_info_by_id(id: number) {
    const data: any = await this.get_schematics();
    const list = data.data.filter((x: any) => x.schematicID === id);
    if (list) {
      return list;
    } else {
      return null;
    }
  }
  async get_schematic_by_output_id(id: number) {
    const data: any = await this.get_schematics();
    const schematic_for_this_type_id = data.data.find((x: any) => x.typeID === id && x.isInput === 0);
    if (schematic_for_this_type_id) {
      return schematic_for_this_type_id;
    } else {
      return null;
    }
  }
  async get_schematics_pin_info() {
    return await typesMutex.runExclusive(async () => {
      let data = await cache.get("schematics_pin_map");
      if (data) {
        return data;
      }
      let url = `${this.baseUrl}/planetSchematicsPinMap.csv`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert CSV to JSON array
      data = Papa.parse(text, {
        dynamicTyping: true,
        header: true,
      });
      // Save to cache
      cache.put("schematics_pin_map", data, 60 * 60 * 1000 /* 1 hour */);
      return data;
    });
  }

  async get_schematics_pin_info_by_id(id: number) {
    const data: any = await this.get_schematics_pin_info();
    const list = data.data.filter((x: any) => x.schematicID === id);
    if (list) {
      return list;
    } else {
      return null;
    }
  }
  async get_solarsystems() {
    return await typesMutex.runExclusive(async () => {
      let data = await cache.get("solarsystems");
      if (data) return data;
      // Get stations YAML file from Azure storage
      let url = `${this.baseUrl}/mapSolarSystems.yaml`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert YAML to JSON
      data = await load(text);
      // Save to cache
      cache.put("solarsystems", data, 60 * 60 * 1000 /* 1 hour */);
      return data;
    });
  }

  async get_solarsystem_by_id(id: number) {
    const data: any = await this.get_solarsystems();
    if (Object.prototype.hasOwnProperty.call(data, id)) {
      return data[id];
    } else {
      return null;
    }
  }
  async get_solarsystem_in_constellation(id: number) {
    const data: any = await this.get_solarsystems();
    if (data) {
      return (
        Object.entries(data)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([_, obj]: any) => obj.constellationID === id)
          .map(([id, obj]: any) => ({ ...obj, solarSystemID: Number(id) }))
      );
    } else {
      return null;
    }
  }
  async get_solarsystems_by_string(search: string, strict: boolean) {
    const data: any = await this.get_solarsystems();
    if (search == null) {
      return Object.entries(data).map(([id, obj]: any) => ({ ...obj, solarSystemID: Number(id) }));
    }
    let systems = undefined;
    if (strict) {
      systems = Object.entries(data)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, obj]: any) => String(obj.name.en).toLowerCase() === search.toLowerCase())
        .map(([id, obj]: any) => ({ ...obj, solarSystemID: Number(id) }));
    } else {
      systems = Object.entries(data)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, obj]: any) => String(obj.name.en).toLowerCase().indexOf(search.toLowerCase()) > -1)
        .map(([id, obj]: any) => ({ ...obj, solarSystemID: Number(id) }));
    }
    if (systems !== undefined) {
      return systems;
    } else {
      return null;
    }
  }

  async get_constellations() {
    return await typesMutex.runExclusive(async () => {
      let data = await cache.get("constellations");
      if (data) return data;
      // Get stations YAML file from Azure storage
      let url = `${this.baseUrl}/mapConstellations.yaml`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert YAML to JSON
      data = await load(text);
      // Save to cache
      cache.put("constellations", data, 60 * 60 * 1000 /* 1 hour */);
      return data;
    });
  }

  async get_constellation_by_id(id: number) {
    const data: any = await this.get_constellations();
    if (Object.prototype.hasOwnProperty.call(data, id)) {
      return data[id];
    } else {
      return null;
    }
  }

  //ImageJSON
  async get_images() {
    return await typesMutex.runExclusive(async () => {
      let images = await cache.get("images");
      if (images) {
        return images;
      }
      // Get types CSV file from service
      let url = `${this.baseUrl}/imagesMapping.json`;
      const response = checkStatus(await fetch(url));
      images = JSON.parse(await response.text());
      // Results are a JSON file.
      // Save to cache
      cache.put("images", images, 60 * 60 * 1000 /* 1 hour */);
      return images;
    });
  }
  async get_image_type(type_id: number) {
    const images: any = await this.get_images();
    const availableImgTypes = images.find((x: any) => x.id === type_id);
    if (availableImgTypes !== undefined && availableImgTypes.availableImageTypes) {
      return availableImgTypes.availableImageTypes;
    } else {
      return null;
    }
  }

  //Regions
  async get_regions() {
    return await typesMutex.runExclusive(async () => {
      let data = await cache.get("regions");
      if (data) return data;
      // Get stations YAML file from Azure storage
      let url = `${this.baseUrl}/mapRegions.yaml`;
      const response = checkStatus(await fetch(url));
      const text = await response.text();
      // Convert YAML to JSON
      data = await load(text);
      // Save to cache
      cache.put("regions", data, 60 * 60 * 1000 /* 1 hour */);
      return data;
    });
  }
  async get_region_by_id(id: number) {
    const data: any = await this.get_regions();
    if (Object.prototype.hasOwnProperty.call(data, id)) {
      return data[id];
    } else {
      return null;
    }
  }
  async get_region_by_string(search: string, strict: boolean) {
    const data: any = await this.get_regions();

    if (search == null) {
      return Object.entries(data).map(([id, obj]: any) => ({ ...obj, regionID: Number(id) }));
    }
    let regionData = undefined;
    if (strict) {
      regionData = Object.entries(data)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, obj]: any) => String(obj.name.en).toLowerCase() === search.toLowerCase())
        .map(([id, obj]: any) => ({ ...obj, regionID: Number(id) }));
    } else {
      regionData = Object.entries(data)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, obj]: any) => String(obj.name.en).toLowerCase().indexOf(search.toLowerCase()) > -1)
        .map(([id, obj]: any) => ({ ...obj, regionID: Number(id) }));
    }
    if (regionData !== undefined) {
      return regionData;
    } else {
      return null;
    }
  }
}

function getJsonObjects(csv: any) {
  const headers = csv.data[0];
  let objects: any = {};
  csv.data.slice(1).forEach((row: string[]) => {
    objects[row[0]] = getJsonObject(headers, row);
  });
  return objects;
}

function getJsonObject(headers: string[], row: any[]) {
  let obj: { [key: string]: any } = {};
  headers.forEach((header, index) => {
    let propName = header;
    let propValue = row[index];
    switch (header) {
      case "typeID":
        propName = "id";
        break;
      case "typeName":
        propName = "name";
        break;
      case "published":
        propValue = row[index] === 1;
        break;
    }
    obj[propName] = propValue;
  });
  return obj;
}

function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    throw new Error(response.statusText);
  }
}
